<template>
  <DModal
    :show-close="false"
    :isBgOverlay="true"
    :appendToBody="true"
    :placeInTop="true"
    @close="$emit('close')"
  >
    <div class="sds-schedule text-dark" slot="insertComponent">
      <div class="sds-schedule__header">
        <h2>Skemalæg brikker</h2>
        <div class="font-large icon__bg-circle sds-schedule__header__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="sds-schedule__content">
        <p class="sds-schedule__period">{{ periodLabel }}</p>

        <div class="sds-schedule__common" v-if="rows.length > 1">
          <span class="sds-schedule__common__label">Samme tid for alle:</span>
          <input type="time" v-model="commonStart" class="sds-schedule__input sds-schedule__input--common">
          <span class="sds-schedule__common__sep">–</span>
          <input type="time" v-model="commonEnd" class="sds-schedule__input sds-schedule__input--common">
        </div>

        <p v-if="rows.length === 0" class="sds-schedule__empty">Der er ingen brikker at skemalægge.</p>

        <table v-else class="do-table do-table--striped sds-schedule__table">
          <thead>
            <tr>
              <th class="sds-schedule__cell-name">Brik</th>
              <th class="sds-schedule__cell-classes">Klasser</th>
              <th class="sds-schedule__cell-time">Fra</th>
              <th class="sds-schedule__cell-time">Til</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.subject.id"
                :class="{'sds-schedule__row--error': row.error}">
              <td class="sds-schedule__cell-name">
                <span class="sds-schedule__pill" :style="{backgroundColor: row.subject.color}">{{ row.subject.name }}</span>
              </td>
              <td class="sds-schedule__cell-classes">{{ classNames(row.subject) || '–' }}</td>
              <td class="sds-schedule__cell-time">
                <input type="time" v-model="row.start" class="sds-schedule__input" @change="row.error = false">
              </td>
              <td class="sds-schedule__cell-time">
                <input type="time" v-model="row.end" class="sds-schedule__input" @change="row.error = false">
              </td>
            </tr>
          </tbody>
        </table>

        <p class="sds-schedule__error" v-if="timeError">Sluttid skal være efter starttid for alle brikker.</p>
      </div>

      <div class="sds-schedule__footer">
        <button class="do-bttn" @click="$emit('close')" :disabled="isScheduling">Annuller</button>
        <button
          class="do-bttn do-bttn--submit"
          :class="{'do-bttn--loading': isScheduling}"
          :disabled="isScheduling || rows.length === 0"
          @click="runSchedule">Skemalæg</button>
      </div>
    </div>
  </DModal>
</template>

<script>
import moment from 'moment'
import eventbus from '../../eventbus'
import docendo from '../../docendo'
import DModal from '../common/DModal'

export default {
  name: 'special-day-schedule-modal',
  components: {DModal},
  props: {
    schoolId: {
      default: null
    },
    specialDay: {
      type: Object,
      default: () => ({})
    },
    subjects: {
      type: Array,
      default: () => []
    },
    klassCalendars: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      isScheduling: false,
      timeError: false,
      commonStart: '08:00',
      commonEnd: '12:00',
      rows: this.subjects.map(subject => ({
        subject: subject,
        start: '08:00',
        end: '12:00',
        error: false
      }))
    }
  },
  computed: {
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    klassHash () {
      return this.klassCalendars.reduce((obj, klass) => {
        obj[klass.id] = klass
        return obj
      }, {})
    },
    dates () {
      const dates = []
      if (!this.specialDay || !this.specialDay.start_date) {
        return dates
      }
      const end = moment(this.specialDay.end_date || this.specialDay.start_date)
      let cur = moment(this.specialDay.start_date)
      while (cur.isSameOrBefore(end, 'day')) {
        dates.push(cur.format('YYYY-MM-DD'))
        cur = cur.add(1, 'day')
      }
      return dates
    },
    periodLabel () {
      if (this.dates.length === 0) {
        return ''
      }
      const start = moment(this.dates[0])
      const end = moment(this.dates[this.dates.length - 1])
      if (start.isSame(end, 'day')) {
        return 'Brikkerne skemalægges ' + start.format('ddd D. MMM YYYY')
      }
      return 'Brikkerne skemalægges hver dag ' + start.format('D. MMM') + ' – ' + end.format('D. MMM YYYY') + ' (' + this.dates.length + ' dage)'
    }
  },
  watch: {
    // Når man udfylder den fælles tid, opdateres alle brikker med det samme.
    commonStart (val) {
      this.rows.forEach(row => {
        row.start = val
        row.error = false
      })
      this.timeError = false
    },
    commonEnd (val) {
      this.rows.forEach(row => {
        row.end = val
        row.error = false
      })
      this.timeError = false
    }
  },
  mounted () {
    document.body.classList.add('no-scroll')
  },
  beforeDestroy () {
    document.body.classList.remove('no-scroll')
  },
  methods: {
    classNames (subject) {
      const ids = []
      if (subject.calendar_id) {
        ids.push(subject.calendar_id)
      }
      if (Array.isArray(subject.calendar_ids)) {
        subject.calendar_ids.forEach(id => ids.push(id))
      }
      const seen = {}
      const names = []
      ids.forEach(id => {
        if (seen[id] || !this.klassHash[id]) {
          return
        }
        seen[id] = true
        names.push(this.klassHash[id].name)
      })
      return names.join(', ')
    },
    buildCalendarIds (subject) {
      const ids = []
      const add = id => {
        if (id && ids.indexOf(id) === -1) {
          ids.push(id)
        }
      }
      add(subject.calendar_id)
      const list = Array.isArray(subject.calendar_ids) ? subject.calendar_ids : []
      list.forEach(id => {
        const cal = this.allCalendarsInHash[id]
        if (!cal || cal.type !== 'school') {
          add(id)
        }
      })
      return ids
    },
    runSchedule () {
      if (this.isScheduling || this.rows.length === 0) {
        return
      }
      // Validér tider — sluttid skal ligge efter starttid.
      this.timeError = false
      let hasError = false
      this.rows.forEach(row => {
        const invalid = !row.start || !row.end || row.end <= row.start
        row.error = invalid
        if (invalid) {
          hasError = true
        }
      })
      if (hasError) {
        this.timeError = true
        return
      }

      const tasks = []
      this.rows.forEach(row => {
        const subject = row.subject
        const calendarIds = this.buildCalendarIds(subject)
        this.dates.forEach(date => {
          const start = moment(date + ' ' + row.start, 'YYYY-MM-DD HH:mm').format()
          const end = moment(date + ' ' + row.end, 'YYYY-MM-DD HH:mm').format()
          tasks.push({subject: subject, calendarIds: calendarIds, start: start, end: end})
        })
      })

      if (tasks.length === 0) {
        this.$emit('close')
        return
      }

      this.isScheduling = true
      let completed = 0
      let failed = 0
      const total = tasks.length
      const onDone = () => {
        completed++
        if (completed === total) {
          this.isScheduling = false
          if (failed > 0) {
            eventbus.toast = {text: 'Nogle brikker kunne ikke skemalægges (' + failed + ' af ' + total + ')', success: false}
          } else {
            eventbus.toast = {text: 'Brikkerne blev skemalagt', success: true}
          }
          this.$emit('scheduled')
          this.$emit('close')
        }
      }
      tasks.forEach(task => {
        docendo.createEvent(
          this.schoolId, task.subject.calendar_id, task.calendarIds, task.subject.id,
          task.start, task.end, '', '', '', '',
          () => onDone(),
          () => {
            failed++
            onDone()
          }
        )
      })
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.sds-schedule {
  width: calc(100vw - 4rem);
  max-width: 720px;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.sds-schedule__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #eee;
}

.sds-schedule__header h2 {
  margin: 0;
}

.sds-schedule__header__close {
  cursor: pointer;
}

.sds-schedule__content {
  padding: 1rem 1.5rem;
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.sds-schedule__period {
  margin: 0 0 1rem 0;
  font-size: 13px;
  color: var(--grey);
}

.sds-schedule__empty {
  font-style: italic;
  color: var(--grey);
}

.sds-schedule__common {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
  justify-content: flex-end;
  gap: 1.3rem;
  padding-right: 30px;
}

.sds-schedule__common__label {
  font-size: 13px;
  font-weight: bold;
  color: var(--grey);
  white-space: nowrap;
}

.sds-schedule__common__sep {
  color: var(--grey);
}

.sds-schedule__input--common {
  width: 96px;
  flex: 0 0 auto;
}

.sds-schedule__table {
  width: 100%;
}

.sds-schedule__cell-name {
  width: 32%;
}

.sds-schedule__cell-classes {
  width: 28%;
}

.sds-schedule__cell-time {
  width: 20%;
}

.sds-schedule__pill {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 4px 8px;
  border-radius: 4px;
  color: #fff;
  font-weight: bold;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, .3);
}

.sds-schedule__input {
  height: 34px;
  background-color: #fff;
  border: 1px solid #acb6be;
  border-radius: 4px;
  padding: 0 0.5rem;
  width: 100%;
  max-width: 100px;
}

.sds-schedule__row--error .sds-schedule__input {
  border-color: var(--danger);
}

.sds-schedule__error {
  color: var(--danger);
  font-size: 13px;
  margin: 0.75rem 0 0 0;
}

.sds-schedule__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #eee;
}
</style>



// WEBPACK FOOTER //
// SpecialDayScheduleModal.vue?55d39eec